import { c as _c } from "react/compiler-runtime";
import { useTranslation } from '@/hooks/use-translation';
import { benchAdjustment, isWorthAdjusting } from '@/lib/piano/touch-design/bench-adjustment';
import { cn } from '@/lib/utils';
import { formatSignedWeightValue, formatWeightValue } from '@/lib/format/format-weight';
export interface AdjustmentValueProps {
  actualValue: number | null;
  targetValue: number | null;
}

/*
 * The current weight and the target each take a slot four characters wide,
 * set to the right: down a column the units, the commas and the tenths stand
 * one under the other, and every change starts at the same place, just after
 * its target, rather than at the far edge of a wide column.
 */
const slotClassName = 'min-w-[4ch] text-right';
const Arrow = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span aria-hidden="true" className="text-xs text-ptl-ink-3">→</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/**
 * One adjustment: where the key stands, where it should go, and by how much.
 *
 * The pill is coloured by the sign, never by the column — brass to add weight,
 * red to take it off — because that is the only thing a technician acts on. On
 * paper the pill flattens to plain text, as the printed sheet of the design
 * shows it: a tinted lozenge per row is a lot of toner for a sign. There it is
 * set smaller and lighter than the target, which is the figure in bold, so
 * that the two are not read for one another.
 *
 * A key that was never read still gets its target — springs fitted where
 * none were measured are set to it all the same — but no change to make.
 */
export const AdjustmentValue: React.FC<AdjustmentValueProps> = t0 => {
  const $ = _c(55);
  const {
    actualValue,
    targetValue
  } = t0;
  const {
    currentLanguage,
    t
  } = useTranslation();
  if (targetValue === null) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className={cn(slotClassName, "text-ptl-ink-3")}>—</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[1] !== actualValue || $[2] !== currentLanguage || $[3] !== t || $[4] !== targetValue) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const difference = benchAdjustment(actualValue, targetValue);
      if (actualValue === null || difference === null) {
        let t7;
        let t8;
        let t9;
        if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <span className={cn(slotClassName, "text-ptl-ink-3")}>—</span>;
          t8 = <Arrow />;
          t9 = cn(slotClassName, "font-semibold text-foreground");
          $[11] = t7;
          $[12] = t8;
          $[13] = t9;
        } else {
          t7 = $[11];
          t8 = $[12];
          t9 = $[13];
        }
        let t10;
        if ($[14] !== currentLanguage || $[15] !== targetValue) {
          t10 = formatWeightValue(targetValue, currentLanguage);
          $[14] = currentLanguage;
          $[15] = targetValue;
          $[16] = t10;
        } else {
          t10 = $[16];
        }
        let t11;
        if ($[17] !== t10) {
          t11 = <>{t7}{t8}<span className={t9}>{t10}</span></>;
          $[17] = t10;
          $[18] = t11;
        } else {
          t11 = $[18];
        }
        t6 = t11;
        break bb0;
      }
      if (!isWorthAdjusting(difference)) {
        let t7;
        if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = cn(slotClassName, "text-muted-foreground");
          $[19] = t7;
        } else {
          t7 = $[19];
        }
        let t8;
        if ($[20] !== actualValue || $[21] !== currentLanguage) {
          t8 = formatWeightValue(actualValue, currentLanguage);
          $[20] = actualValue;
          $[21] = currentLanguage;
          $[22] = t8;
        } else {
          t8 = $[22];
        }
        let t9;
        if ($[23] !== t8) {
          t9 = <span className={t7}>{t8}</span>;
          $[23] = t8;
          $[24] = t9;
        } else {
          t9 = $[24];
        }
        let t10;
        if ($[25] !== t) {
          t10 = t("unchanged");
          $[25] = t;
          $[26] = t10;
        } else {
          t10 = $[26];
        }
        let t11;
        if ($[27] !== t10) {
          t11 = <span className="font-sans text-xs text-ptl-ink-3">{t10}</span>;
          $[27] = t10;
          $[28] = t11;
        } else {
          t11 = $[28];
        }
        let t12;
        if ($[29] !== t11 || $[30] !== t9) {
          t12 = <>{t9}{t11}</>;
          $[29] = t11;
          $[30] = t9;
          $[31] = t12;
        } else {
          t12 = $[31];
        }
        t6 = t12;
        break bb0;
      }
      let t7;
      if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = cn(slotClassName, "text-muted-foreground");
        $[32] = t7;
      } else {
        t7 = $[32];
      }
      let t8;
      if ($[33] !== actualValue || $[34] !== currentLanguage) {
        t8 = formatWeightValue(actualValue, currentLanguage);
        $[33] = actualValue;
        $[34] = currentLanguage;
        $[35] = t8;
      } else {
        t8 = $[35];
      }
      if ($[36] !== t8) {
        t3 = <span className={t7}>{t8}</span>;
        $[36] = t8;
        $[37] = t3;
      } else {
        t3 = $[37];
      }
      let t9;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <Arrow />;
        t9 = cn(slotClassName, "font-semibold text-foreground");
        $[38] = t4;
        $[39] = t9;
      } else {
        t4 = $[38];
        t9 = $[39];
      }
      let t10;
      if ($[40] !== currentLanguage || $[41] !== targetValue) {
        t10 = formatWeightValue(targetValue, currentLanguage);
        $[40] = currentLanguage;
        $[41] = targetValue;
        $[42] = t10;
      } else {
        t10 = $[42];
      }
      if ($[43] !== t10) {
        t5 = <span className={t9}>{t10}</span>;
        $[43] = t10;
        $[44] = t5;
      } else {
        t5 = $[44];
      }
      const t11 = difference > 0 ? "bg-ptl-delta-plus text-ptl-delta-plus-foreground" : "bg-ptl-delta-minus text-ptl-delta-minus-foreground";
      if ($[45] !== t11) {
        t1 = cn("ml-1 rounded-full px-[7px] py-0.5 text-xs", "print:ml-0.5 print:bg-transparent print:px-0 print:text-[10.5px] print:text-muted-foreground", t11);
        $[45] = t11;
        $[46] = t1;
      } else {
        t1 = $[46];
      }
      t2 = formatSignedWeightValue(difference, currentLanguage);
    }
    $[1] = actualValue;
    $[2] = currentLanguage;
    $[3] = t;
    $[4] = targetValue;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[47] !== t1 || $[48] !== t2) {
    t7 = <span className={t1}>{t2}</span>;
    $[47] = t1;
    $[48] = t2;
    $[49] = t7;
  } else {
    t7 = $[49];
  }
  let t8;
  if ($[50] !== t3 || $[51] !== t4 || $[52] !== t5 || $[53] !== t7) {
    t8 = <>{t3}{t4}{t5}{t7}</>;
    $[50] = t3;
    $[51] = t4;
    $[52] = t5;
    $[53] = t7;
    $[54] = t8;
  } else {
    t8 = $[54];
  }
  return t8;
};